import { FinancialPlanAccounts } from '../types/financialPlan';
import { IRS_LIMITS_2026 } from './financialPlanDefaults';

/** Age when Roth IRA catch-up contribution applies (2026 rules). */
export const ROTH_IRA_CATCH_UP_AGE = 50;

function clampNonNeg(n: number): number {
  return Number.isFinite(n) ? Math.max(0, n) : 0;
}

/** Maximum annual Roth IRA contribution for a plan age. */
export function rothIraAnnualMaxAtAge(age: number): number {
  return age >= ROTH_IRA_CATCH_UP_AGE ? IRS_LIMITS_2026.rothIraCatchUp : IRS_LIMITS_2026.rothIra;
}

export function rothIraMonthlyMaxAtAge(age: number): number {
  return rothIraAnnualMaxAtAge(age) / 12;
}

/** Cap a desired monthly Roth contribution; returns capped amount and overflow to taxable. */
export function capRothIraMonthlyContribution(
  desiredMonthly: number,
  age: number,
): { rothMonthly: number; overflowToTaxable: number } {
  const max = rothIraMonthlyMaxAtAge(age);
  const desired = clampNonNeg(desiredMonthly);
  if (desired <= max) return { rothMonthly: desired, overflowToTaxable: 0 };
  return { rothMonthly: max, overflowToTaxable: desired - max };
}

/** Cap Roth IRA in a monthly contribution map; overflow routes to taxable brokerage. */
export function applyRothIraCapToMonthlyContributions(
  monthly: Record<keyof FinancialPlanAccounts, number>,
  age: number,
): Record<keyof FinancialPlanAccounts, number> {
  const { rothMonthly, overflowToTaxable } = capRothIraMonthlyContribution(monthly.rothIra, age);
  if (overflowToTaxable <= 0) return monthly;
  return {
    ...monthly,
    rothIra: rothMonthly,
    taxable: clampNonNeg(monthly.taxable) + overflowToTaxable,
  };
}

/** Cap Roth portion of an annual lump-sum split after monthly contributions are accounted for. */
export function applyRothIraCapToLumpSumSplit(
  split: Record<keyof FinancialPlanAccounts, number>,
  age: number,
  rothContributedAnnual = 0,
): Record<keyof FinancialPlanAccounts, number> {
  const maxAnnual = rothIraAnnualMaxAtAge(age);
  const room = Math.max(0, maxAnnual - clampNonNeg(rothContributedAnnual));
  const rothDesired = clampNonNeg(split.rothIra);
  if (rothDesired <= room) return split;
  const overflow = rothDesired - room;
  return {
    ...split,
    rothIra: room,
    taxable: clampNonNeg(split.taxable) + overflow,
  };
}
